import type { KeyboardEvent, ReactNode } from 'react';
import type { UnassignedMail } from '@nest-recruit/shared';
import { Pager } from '../../../../components/Pager';
import { SkeletonLines } from '../../../../components/Skeleton';
import { formatDateTimeLong, formatNumber, formatRelative } from '../../../../lib/format';
import styles from './WaitingList.module.css';

/** The DOM id of a mail's row, so the arrow keys can move the focus with the opened email. */
export function mailRowId(id: string): string {
  return `inbox-mail-${id}`;
}

/**
 * Inbound mail's left pane (Option A): every email waiting, newest first — who sent it, when, the subject and its
 * first lines. ↑ ↓ walk the list and open each one (06-qol §2). Fifty a page, with page buttons past that.
 */
export function WaitingList({
  mail,
  total,
  page,
  pageSize,
  openId,
  onOpen,
  onPage,
}: {
  readonly mail: readonly UnassignedMail[] | undefined;
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly openId: string | null;
  readonly onOpen: (id: string) => void;
  readonly onPage: (page: number) => void;
}): ReactNode {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>): void => {
    if (!mail || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const at = mail.findIndex((each) => each.messageId === openId);
    const next = mail[at + (event.key === 'ArrowDown' ? 1 : -1)] ?? (at < 0 ? mail[0] : undefined);
    if (!next) return;
    event.preventDefault();
    onOpen(next.messageId);
    document.getElementById(mailRowId(next.messageId))?.focus();
  };

  return (
    <section className={styles.pane} aria-label="Waiting to be filed">
      <header className={styles.head}>
        <h2 className={styles.title}>Waiting to be filed</h2>
        <span className={styles.count}>{formatNumber(total)}</span>
      </header>
      {!mail ? (
        <div className={styles.loading}>
          <SkeletonLines count={6} />
        </div>
      ) : (
        <ul className={styles.rows} aria-label="Emails waiting" onKeyDown={onKeyDown}>
          {mail.map((each) => (
            <li key={each.messageId}>
              <button type="button" id={mailRowId(each.messageId)} className={styles.row} aria-current={each.messageId === openId || undefined} onClick={() => onOpen(each.messageId)}>
                <span className={styles.top}>
                  <span className={styles.from}>{each.from}</span>
                  <span className={styles.when} title={formatDateTimeLong(each.sentAt ?? each.receivedAt)}>
                    {formatRelative(each.sentAt ?? each.receivedAt)}
                  </span>
                </span>
                <span className={styles.subject}>{each.subject}</span>
                {each.excerpt !== '' && <span className={styles.excerpt}>{each.excerpt}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
      {pageCount > 1 && mail && <Pager page={page} pageCount={pageCount} pageSize={pageSize} rowCount={mail.length} total={total} onPage={onPage} />}
    </section>
  );
}
